import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { 
  Plus, Minus, Move, Edit, Trash, Link, Unlink, PenTool, Search, 
  Image, Mic, Video, Download, ChevronRight, X, Copy, Tag, Star
} from 'lucide-react';
import { getFromStorage, saveToStorage } from '../utils/storage';
import { ENDLESS_NOTES, STORAGE_KEYS } from '../utils/constants';

/**
 * Endless Notes Canvas Bileşeni
 * Sonsuz bir kanvas üzerinde düşünce, görev, soru ve içgörü notları oluşturma
 * ve bağlantılandırma imkanı sunar.
 * 
 * @param {Object} props - Bileşen özellikleri
 * @param {string} props.currentNodeType - Aktif düğüm türü (thought, task, question, insight, vs.)
 * @param {boolean} props.darkMode - Karanlık mod etkin mi?
 */
const EndlessNotesCanvas = forwardRef((props, ref) => {
  // Props
  const { currentNodeType = 'thought', darkMode = false } = props;
  
  // Refs
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const contextMenuRef = useRef(null);
  const nodeEditModalRef = useRef(null);
  const connectionRef = useRef(null);
  const animationFrameRef = useRef(null);
  const selectedNodesRef = useRef(new Set());
  const dragStartPositionRef = useRef({ x: 0, y: 0 });
  const fontSizeRef = useRef(12);
  
  // Canvas durumu
  const [nodes, setNodes] = useState([]);
  const [connections, setConnections] = useState([]);
  const [canvas, setCanvas] = useState({
    offsetX: 0,
    offsetY: 0,
    scale: 1,
    isDragging: false,
    isConnecting: false,
    isDrawing: false,
    isSelecting: false
  });
  
  // UI durumu
  const [currentAction, setCurrentAction] = useState(ENDLESS_NOTES.ACTIONS.MOVE);
  const [selectedNodeIds, setSelectedNodeIds] = useState([]);
  const [multipleSelection, setMultipleSelection] = useState(false);
  const [contextMenu, setContextMenu] = useState({
    isVisible: false,
    x: 0,
    y: 0,
    targetId: null,
    targetType: null // 'node', 'connection', 'canvas'
  });
  const [editingNode, setEditingNode] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [connectingNode, setConnectingNode] = useState(null);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [connectionType, setConnectionType] = useState(ENDLESS_NOTES.CONNECTION_TYPES[0].id);
  const [isGridVisible, setIsGridVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  
  // Düğüm düzenleme durumu
  const [nodeEditForm, setNodeEditForm] = useState({
    id: null,
    content: '',
    type: 'thought',
    color: '#6366F1',
    tags: [],
    tagInput: '',
    isStarred: false
  });
  
  // Redux'tan oturum ve kimlik doğrulama bilgilerini al (demo: direkt state kullanıyoruz)
  const [userId] = useState('demo-user');
  
  // ---------------------------------------------------------------------------
  // İlk Yükleme ve Veri Yükleme/Kaydetme
  // ---------------------------------------------------------------------------
  
  // İlk yükleme
  useEffect(() => {
    loadCanvasData();
    
    // Örnek veriler - yeni kullanıcılar için başlangıç notları
    const hasData = getFromStorage(STORAGE_KEYS.ENDLESS_NOTES_DATA, null);
    if (!hasData) {
      setNodes([
        {
          id: 'welcome',
          type: 'insight',
          content: 'Endless Notes\'a Hoş Geldiniz',
          position: { offsetX: -150, offsetY: -100 },
          color: '#EC4899',
          tags: ['başlangıç'],
          isStarred: true
        },
        {
          id: 'explanation',
          type: 'thought',
          content: 'Bu merkeziyetsiz zihin sarayınızdır. Düşüncelerinizi düzenleyebilir ve blockchain\'de saklayabilirsiniz.',
          position: { offsetX: -100, offsetY: 50 },
          color: '#6366F1',
          tags: ['bilgi'],
          isStarred: false
        },
        {
          id: 'tip',
          type: 'question',
          content: 'İpucu: Notları sürükleyebilir, bağlantılar kurabilir ve farklı not tipleri oluşturabilirsiniz.',
          position: { offsetX: 150, offsetY: -50 },
          color: '#F59E0B',
          tags: ['ipucu'],
          isStarred: false
        }
      ]);
      
      setConnections([
        {
          id: 'conn-1',
          sourceId: 'welcome',
          targetId: 'explanation',
          type: 'relates',
          color: '#6B7280'
        },
        {
          id: 'conn-2',
          sourceId: 'welcome',
          targetId: 'tip',
          type: 'example',
          color: '#8B5CF6'
        }
      ]);
    }
    
    // Olay dinleyicileri
    window.addEventListener('resize', handleResize);
    document.addEventListener('keydown', handleKeyDown);
    
    // Apply font scaling to canvas text 
    const handleFontSizeChange = () => {
      const fontScale = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--font-size-scale')) || 1;
      fontSizeRef.current = 12 * fontScale; // Adjust base font size for notes
      drawCanvas(); // Redraw canvas with new font size
    };
    
    // Listen for font size change events
    window.addEventListener('fontSizeChanged', handleFontSizeChange);
    handleFontSizeChange(); // Initial application

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('fontSizeChanged', handleFontSizeChange);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);
  
  // Kanvas verilerini yükle
  const loadCanvasData = async () => {
    setIsLoading(true);
    try {
      const savedData = await getFromStorage(STORAGE_KEYS.ENDLESS_NOTES_DATA, null);
      
      if (savedData && savedData.canvas) {
        // Kaydedilmiş notları ve bağlantıları yükle
        setNodes(savedData.canvas.nodes || []);
        setConnections(savedData.canvas.connections || []);
        setCanvas(prev => ({
          ...prev,
          offsetX: savedData.canvas.offsetX || 0,
          offsetY: savedData.canvas.offsetY || 0,
          scale: savedData.canvas.scale || 1
        }));
      }
    } catch (error) {
      console.error("Canvas verilerini yükleme hatası:", error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Kanvas verilerini dışa aç
  useImperativeHandle(ref, () => ({
    // Kanvas verilerini al
    getCanvasData: () => {
      return {
        nodes,
        connections,
        offsetX: canvas.offsetX,
        offsetY: canvas.offsetY,
        scale: canvas.scale,
        lastUpdated: new Date().toISOString()
      };
    },
    
    // Kanvas verilerini yükle
    loadCanvasData: (data) => {
      if (data.nodes) setNodes(data.nodes);
      if (data.connections) setConnections(data.connections);
      if (data.offsetX !== undefined || data.offsetY !== undefined || data.scale !== undefined) {
        setCanvas(prev => ({
          ...prev,
          offsetX: data.offsetX !== undefined ? data.offsetX : prev.offsetX,
          offsetY: data.offsetY !== undefined ? data.offsetY : prev.offsetY,
          scale: data.scale !== undefined ? data.scale : prev.scale
        }));
      }
    },
    
    // Yeni düğüm ekle
    addNode: (content, type = 'thought', position = null) => {
      const newNode = createNewNode(content, type, position);
      setNodes(prev => [...prev, newNode]);
      return newNode.id;
    },
    
    // Görünümü sıfırla
    resetView: () => {
      setCanvas(prev => ({
        ...prev,
        offsetX: 0,
        offsetY: 0,
        scale: 1
      }));
    },
    
    // Düğüme odaklan
    focusOnNode: (nodeId) => {
      const node = nodes.find(n => n.id === nodeId);
      if (node) {
        setCanvas(prev => ({
          ...prev,
          offsetX: -node.position.offsetX,
          offsetY: -node.position.offsetY,
          scale: 1
        }));
      }
    }
  }));
  
  // Arama sonuçlarını filtrele
  const filteredNodes = searchTerm 
    ? nodes.filter(node => 
        node.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (node.tags && node.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
      )
    : nodes;
  
  // ---------------------------------------------------------------------------
  // Düğüm ve Bağlantı İşlemleri
  // ---------------------------------------------------------------------------
  
  // Yeni düğüm oluştur
  const createNewNode = (content, type = 'thought', position = null) => {
    const id = `node-${Date.now()}`;
    const nodeType = ENDLESS_NOTES.NODE_TYPES[type] ? type : 'thought';
    const nodeColor = ENDLESS_NOTES.NODE_TYPES[nodeType].color;
    
    // Pozisyon belirtilmemişse ekranın ortasına yerleştir
    let posX = 0, posY = 0;
    
    if (position) {
      posX = position.x;
      posY = position.y;
    } else if (containerRef.current) {
      // Ekran ortasına göre hesapla
      const centerX = containerRef.current.clientWidth / 2;
      const centerY = containerRef.current.clientHeight / 2;
      
      // Canvas ofseti ve ölçeğini hesaba kat
      posX = (centerX / canvas.scale) - canvas.offsetX;
      posY = (centerY / canvas.scale) - canvas.offsetY;
    }
    
    return {
      id,
      type: nodeType,
      content: content || 'Yeni not...',
      position: {
        offsetX: posX,
        offsetY: posY
      },
      color: nodeColor,
      tags: [],
      isStarred: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  };
  
  // Düğüm ekle
  const addNode = (e) => {
    if (currentAction !== ENDLESS_NOTES.ACTIONS.MOVE) return;
    
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - canvasRect.left) / canvas.scale - canvas.offsetX;
    const y = (e.clientY - canvasRect.top) / canvas.scale - canvas.offsetY;
    
    const newNode = createNewNode('', currentNodeType, { x, y });
    setNodes(prev => [...prev, newNode]);
    setEditingNode(newNode);
    
    // Düzenleme modalını göster
    setNodeEditForm({
      id: newNode.id,
      content: '',
      type: newNode.type,
      color: newNode.color,
      tags: [],
      tagInput: '',
      isStarred: false
    });
  };
  
  // Düğüm sil
  const deleteNode = (nodeId) => {
    // İlgili bağlantıları bul ve sil
    const updatedConnections = connections.filter(
      conn => conn.sourceId !== nodeId && conn.targetId !== nodeId
    );
    
    // Düğümü sil
    const updatedNodes = nodes.filter(node => node.id !== nodeId);
    
    setConnections(updatedConnections);
    setNodes(updatedNodes);
    setSelectedNodeIds([]);
  };
  
  // Bağlantı ekle
  const addConnection = (sourceId, targetId) => {
    // Kendisine bağlantı kurulamaz
    if (sourceId === targetId) return;
    
    // Zaten var olan bir bağlantı mı?
    const connectionExists = connections.some(
      conn => (conn.sourceId === sourceId && conn.targetId === targetId) ||
              (conn.sourceId === targetId && conn.targetId === sourceId)
    );
    
    if (connectionExists) return;
    
    // Yeni bağlantı oluştur
    const connectionType = ENDLESS_NOTES.CONNECTION_TYPES.find(type => type.id === connectionType) || 
                           ENDLESS_NOTES.CONNECTION_TYPES[0];
    
    const newConnection = {
      id: `conn-${Date.now()}`,
      sourceId,
      targetId,
      type: connectionType.id,
      color: connectionType.color
    };
    
    setConnections(prev => [...prev, newConnection]);
  };
  
  // Bağlantı sil
  const deleteConnection = (connectionId) => {
    setConnections(prev => prev.filter(conn => conn.id !== connectionId));
  };
  
  // Düğüm güncelle
  const updateNode = (nodeId, updates) => {
    setNodes(prev => prev.map(node => 
      node.id === nodeId 
        ? { ...node, ...updates, updatedAt: new Date().toISOString() } 
        : node
    ));
  };
  
  // ---------------------------------------------------------------------------
  // Olay İşleyicileri
  // ---------------------------------------------------------------------------
  
  // Fare Tıklama
  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // Sadece sol tık
    
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - canvasRect.left;
    const mouseY = e.clientY - canvasRect.top;
    
    dragStartPositionRef.current = { x: mouseX, y: mouseY };
    
    // Context menüyü kapat
    setContextMenu(prev => ({ ...prev, isVisible: false }));
    
    // Hangi işlemin yapılacağına karar ver
    if (currentAction === ENDLESS_NOTES.ACTIONS.MOVE) {
      setCanvas(prev => ({ ...prev, isDragging: true }));
    } else if (currentAction === ENDLESS_NOTES.ACTIONS.CONNECT && connectingNode) {
      // Bağlantı çizme modu
      setCanvas(prev => ({ ...prev, isConnecting: true }));
    }
  };
  
  // Fare Hareket
  const handleMouseMove = (e) => {
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - canvasRect.left;
    const mouseY = e.clientY - canvasRect.top;
    
    if (canvas.isDragging) {
      // Kanvası sürükle
      const deltaX = mouseX - dragStartPositionRef.current.x;
      const deltaY = mouseY - dragStartPositionRef.current.y;
      
      setCanvas(prev => ({
        ...prev,
        offsetX: prev.offsetX + (deltaX / prev.scale),
        offsetY: prev.offsetY + (deltaY / prev.scale)
      }));
      
      dragStartPositionRef.current = { x: mouseX, y: mouseY };
    } else if (canvas.isConnecting && connectingNode) {
      // Bağlantı çizme
      const connectionEndX = (mouseX / canvas.scale) - canvas.offsetX;
      const connectionEndY = (mouseY / canvas.scale) - canvas.offsetY;
      
      // Bağlantı referansını güncelle
      connectionRef.current = {
        start: {
          x: connectingNode.position.offsetX,
          y: connectingNode.position.offsetY
        },
        end: {
          x: connectionEndX,
          y: connectionEndY
        }
      };
      
      // Yeniden çiz
      drawCanvas();
    }
  };
  
  // Fare Bırakma
  const handleMouseUp = (e) => {
    if (canvas.isConnecting && connectingNode && hoveredNodeId) {
      // Bağlantı tamamlandı
      addConnection(connectingNode.id, hoveredNodeId);
    }
    
    // Tüm active durumları sıfırla
    setCanvas(prev => ({
      ...prev,
      isDragging: false,
      isConnecting: false,
      isDrawing: false,
      isSelecting: false
    }));
    
    setConnectingNode(null);
    connectionRef.current = null;
  };
  
  // Fare Çift Tıklama
  const handleDoubleClick = (e) => {
    if (e.target.classList.contains('canvas-container')) {
      addNode(e);
    }
  };
  
  // Fare Sağ Tıklama
  const handleContextMenu = (e) => {
    e.preventDefault();
    
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - canvasRect.left;
    const y = e.clientY - canvasRect.top;
    
    // Hangi öğeye tıklandığını belirle
    let targetId = null;
    let targetType = 'canvas';
    
    // Düğüm kontrolü
    const clickedNode = nodes.find(node => {
      const nodeX = (node.position.offsetX + canvas.offsetX) * canvas.scale;
      const nodeY = (node.position.offsetY + canvas.offsetY) * canvas.scale;
      const nodeWidth = ENDLESS_NOTES.NODE_TYPES[node.type].width * canvas.scale;
      const nodeHeight = ENDLESS_NOTES.NODE_TYPES[node.type].height * canvas.scale;
      
      return (
        x >= nodeX - (nodeWidth / 2) &&
        x <= nodeX + (nodeWidth / 2) &&
        y >= nodeY - (nodeHeight / 2) &&
        y <= nodeY + (nodeHeight / 2)
      );
    });
    
    if (clickedNode) {
      targetId = clickedNode.id;
      targetType = 'node';
    } else {
      // Bağlantı kontrolü
      // Daha karmaşık ve hassas bir hesaplama gerektirir
      // Basitlik için atlıyoruz
    }
    
    setContextMenu({
      isVisible: true,
      x: e.clientX,
      y: e.clientY,
      targetId,
      targetType
    });
  };
  
  // Klavye Olayları
  const handleKeyDown = (e) => {
    // Düğüm düzenleme modalı açıksa işleme
    if (editingNode) return;
    
    if (e.key === 'Delete' && selectedNodeIds.length > 0) {
      // Seçili düğümleri sil
      selectedNodeIds.forEach(id => deleteNode(id));
    } else if (e.key === 'Escape') {
      // Aktif işlemi iptal et
      setSelectedNodeIds([]);
      setCurrentAction(ENDLESS_NOTES.ACTIONS.MOVE);
      setConnectingNode(null);
      connectionRef.current = null;
      setContextMenu(prev => ({ ...prev, isVisible: false }));
    } else if (e.ctrlKey && e.key === 'a') {
      // Tüm düğümleri seç
      e.preventDefault();
      setSelectedNodeIds(nodes.map(node => node.id));
    } else if (e.ctrlKey && e.key === 'c') {
      // Seçili düğümleri kopyala
      if (selectedNodeIds.length > 0) {
        const selectedNodes = nodes.filter(node => selectedNodeIds.includes(node.id));
        const nodesToCopy = selectedNodes.map(node => ({ ...node }));
        localStorage.setItem('clipboard_nodes', JSON.stringify(nodesToCopy));
      }
    } else if (e.ctrlKey && e.key === 'v') {
      // Kopyalanan düğümleri yapıştır
      const clipboardContent = localStorage.getItem('clipboard_nodes');
      if (clipboardContent) {
        try {
          const pastedNodes = JSON.parse(clipboardContent);
          const now = Date.now();
          const offsetX = 50; // Yapıştırma ofseti
          const offsetY = 50;
          
          const newNodes = pastedNodes.map(node => ({
            ...node,
            id: `node-${now}-${Math.random().toString(36).substr(2, 9)}`,
            position: {
              offsetX: node.position.offsetX + offsetX,
              offsetY: node.position.offsetY + offsetY
            }
          }));
          
          setNodes(prev => [...prev, ...newNodes]);
        } catch (error) {
          console.error("Yapıştırma hatası:", error);
        }
      }
    }
  };
  
  // Pencere Boyutu Değiştiğinde
  const handleResize = () => {
    drawCanvas();
  };
  
  // Yakınlaştırma/Uzaklaştırma
  const handleWheel = (e) => {
    e.preventDefault();
    
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - canvasRect.left;
    const mouseY = e.clientY - canvasRect.top;
    
    // Fare konumu - canvas içindeki pozisyon
    const mouseXCanvas = (mouseX / canvas.scale) - canvas.offsetX;
    const mouseYCanvas = (mouseY / canvas.scale) - canvas.offsetY;
    
    // Ölçek faktörü
    const scaleFactor = e.deltaY < 0 ? 1.1 : 0.9;
    const newScale = Math.max(0.1, Math.min(5, canvas.scale * scaleFactor));
    
    // Yeni ofsetler - fareyi odak noktası olarak al
    const newOffsetX = mouseXCanvas - (mouseX / newScale);
    const newOffsetY = mouseYCanvas - (mouseY / newScale);
    
    setCanvas(prev => ({
      ...prev,
      scale: newScale,
      offsetX: -newOffsetX,
      offsetY: -newOffsetY
    }));
  };
  
  // Düğüme Tıklama
  const handleNodeClick = (e, node) => {
    e.stopPropagation();
    
    if (currentAction === ENDLESS_NOTES.ACTIONS.CONNECT) {
      // Bağlantı oluşturma
      if (!connectingNode) {
        setConnectingNode(node);
      } else if (node.id !== connectingNode.id) {
        addConnection(connectingNode.id, node.id);
        setConnectingNode(null);
      }
    } else if (currentAction === ENDLESS_NOTES.ACTIONS.MOVE) {
      // Seçme işlemi
      if (e.ctrlKey || e.metaKey) {
        // Çoklu seçim
        setSelectedNodeIds(prev => 
          prev.includes(node.id) 
            ? prev.filter(id => id !== node.id)
            : [...prev, node.id]
        );
      } else {
        // Tekli seçim
        setSelectedNodeIds([node.id]);
      }
    }
  };
  
  // Düğüm Sürükleme Başlangıcı
  const handleNodeDragStart = (e, node) => {
    e.stopPropagation();
    
    if (currentAction !== ENDLESS_NOTES.ACTIONS.MOVE) return;
    
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - canvasRect.left;
    const mouseY = e.clientY - canvasRect.top;
    
    dragStartPositionRef.current = { 
      x: mouseX, 
      y: mouseY,
      nodeX: node.position.offsetX,
      nodeY: node.position.offsetY
    };
    
    // Eğer düğüm seçili değilse seç
    if (!selectedNodeIds.includes(node.id)) {
      setSelectedNodeIds([node.id]);
    }
  };
  
  // Düğüm Sürükleme
  const handleNodeDrag = (e, node) => {
    if (currentAction !== ENDLESS_NOTES.ACTIONS.MOVE) return;
    
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - canvasRect.left;
    const mouseY = e.clientY - canvasRect.top;
    
    const deltaX = (mouseX - dragStartPositionRef.current.x) / canvas.scale;
    const deltaY = (mouseY - dragStartPositionRef.current.y) / canvas.scale;
    
    // Seçili tüm düğümleri güncelle
    const updatedNodes = nodes.map(n => {
      if (selectedNodeIds.includes(n.id)) {
        return {
          ...n,
          position: {
            offsetX: n.position.offsetX + deltaX,
            offsetY: n.position.offsetY + deltaY
          }
        };
      }
      return n;
    });
    
    setNodes(updatedNodes);
    dragStartPositionRef.current = { x: mouseX, y: mouseY };
  };
  
  // Düğüm Sürükleme Bitişi
  const handleNodeDragEnd = () => {
    // İlgili state'leri sıfırla
  };
  
  // Düğüm Düzenleme
  const handleNodeEdit = (node) => {
    setEditingNode(node);
    setNodeEditForm({
      id: node.id,
      content: node.content,
      type: node.type,
      color: node.color,
      tags: node.tags || [],
      tagInput: '',
      isStarred: node.isStarred || false
    });
  };
  
  // Düğüm Düzenleme Kaydı
  const handleNodeEditSave = () => {
    if (!nodeEditForm.id) return;
    
    updateNode(nodeEditForm.id, {
      content: nodeEditForm.content,
      color: nodeEditForm.color,
      type: nodeEditForm.type,
      tags: nodeEditForm.tags,
      isStarred: nodeEditForm.isStarred
    });
    
    setEditingNode(null);
    setNodeEditForm({
      id: null,
      content: '',
      type: 'thought',
      color: '#6366F1',
      tags: [],
      tagInput: '',
      isStarred: false
    });
  };
  
  // Düğüm Düzenleme İptali
  const handleNodeEditCancel = () => {
    setEditingNode(null);
    
    // Yeni eklenen boş düğümü sil
    if (editingNode && !editingNode.content) {
      deleteNode(editingNode.id);
    }
    
    setNodeEditForm({
      id: null,
      content: '',
      type: 'thought',
      color: '#6366F1',
      tags: [],
      tagInput: '',
      isStarred: false
    });
  };
  
  // Etiket Ekleme
  const handleAddTag = () => {
    if (!nodeEditForm.tagInput.trim()) return;
    
    const newTag = nodeEditForm.tagInput.trim();
    if (!nodeEditForm.tags.includes(newTag)) {
      setNodeEditForm(prev => ({
        ...prev,
        tags: [...prev.tags, newTag],
        tagInput: ''
      }));
    }
  };
  
  // Etiket Silme
  const handleRemoveTag = (tag) => {
    setNodeEditForm(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };
  
  // Düğüm Türü Değiştirme
  const handleNodeTypeChange = (type) => {
    const nodeType = ENDLESS_NOTES.NODE_TYPES[type] ? type : 'thought';
    const nodeColor = ENDLESS_NOTES.NODE_TYPES[nodeType].color;
    
    setNodeEditForm(prev => ({
      ...prev,
      type: nodeType,
      color: nodeColor
    }));
  };
  
  // Görünüme Sıfırlama
  const resetView = () => {
    setCanvas(prev => ({
      ...prev,
      offsetX: 0,
      offsetY: 0,
      scale: 1
    }));
  };
  
  // ---------------------------------------------------------------------------
  // Çizim Fonksiyonları
  // ---------------------------------------------------------------------------
  
  // Canvas'ı çiz
  const drawCanvas = useCallback(() => {
    if (!canvasRef.current) return;
    
    const ctx = canvasRef.current.getContext('2d');
    const width = canvasRef.current.width;
    const height = canvasRef.current.height;
    
    // Kanvası temizle
    ctx.clearRect(0, 0, width, height);
    
    // Izgara çizimi
    if (isGridVisible) {
      drawGrid(ctx, width, height);
    }
    
    // Bağlantıları çiz
    connections.forEach(connection => {
      const sourceNode = nodes.find(node => node.id === connection.sourceId);
      const targetNode = nodes.find(node => node.id === connection.targetId);
      
      if (sourceNode && targetNode) {
        drawConnection(ctx, sourceNode, targetNode, connection);
      }
    });
    
    // Aktif bağlantı çizimini göster
    if (connectionRef.current) {
      ctx.beginPath();
      ctx.moveTo(
        (connectionRef.current.start.x + canvas.offsetX) * canvas.scale,
        (connectionRef.current.start.y + canvas.offsetY) * canvas.scale
      );
      ctx.lineTo(
        (connectionRef.current.end.x + canvas.offsetX) * canvas.scale,
        (connectionRef.current.end.y + canvas.offsetY) * canvas.scale
      );
      ctx.strokeStyle = 'rgba(99, 102, 241, 0.8)';
      ctx.lineWidth = 2 * canvas.scale;
      ctx.stroke();
    }
    
    // Düğümleri çiz - DOM ile
    
    // Animasyon çerçevesi
    animationFrameRef.current = requestAnimationFrame(drawCanvas);
  }, [connections, nodes, canvas, isGridVisible]);
  
  // Izgara çizimi
  const drawGrid = (ctx, width, height) => {
    const gridSize = 50 * canvas.scale;
    const offsetX = (canvas.offsetX * canvas.scale) % gridSize;
    const offsetY = (canvas.offsetY * canvas.scale) % gridSize;
    
    ctx.beginPath();
    ctx.strokeStyle = darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
    ctx.lineWidth = 1;
    
    // Dikey çizgiler
    for (let x = offsetX; x < width; x += gridSize) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
    }
    
    // Yatay çizgiler
    for (let y = offsetY; y < height; y += gridSize) {
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
    }
    
    ctx.stroke();
  };
  
  // Bağlantı çizimi
  const drawConnection = (ctx, sourceNode, targetNode, connection) => {
    const startX = (sourceNode.position.offsetX + canvas.offsetX) * canvas.scale;
    const startY = (sourceNode.position.offsetY + canvas.offsetY) * canvas.scale;
    const endX = (targetNode.position.offsetX + canvas.offsetX) * canvas.scale;
    const endY = (targetNode.position.offsetY + canvas.offsetY) * canvas.scale;
    
    // Bağlantı türüne göre stil
    const connectionType = ENDLESS_NOTES.CONNECTION_TYPES.find(type => type.id === connection.type) || 
                           ENDLESS_NOTES.CONNECTION_TYPES[0];
    
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    
    // Eğri bağlantı
    const cp1x = startX + (endX - startX) / 2;
    const cp1y = startY;
    const cp2x = endX - (endX - startX) / 2;
    const cp2y = endY;
    
    ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, endX, endY);
    
    ctx.strokeStyle = connection.color || connectionType.color;
    ctx.lineWidth = 2 * canvas.scale;
    
    // Seçili bağlantı vurgusu
    if (contextMenu.targetId === connection.id && contextMenu.targetType === 'connection') {
      ctx.lineWidth = 4 * canvas.scale;
    }
    
    ctx.stroke();
    
    // Bağlantı türü etiketi
    const labelX = startX + (endX - startX) / 2;
    const labelY = startY + (endY - startY) / 2 - 10 * canvas.scale;
    
    ctx.font = `${fontSizeRef.current * canvas.scale}px Arial`;
    ctx.fillStyle = connection.color || connectionType.color;
    ctx.textAlign = 'center';
    ctx.fillText(connectionType.label, labelX, labelY);
    
    // Ok ucu
    const angle = Math.atan2(endY - cp2y, endX - cp2x);
    const arrowSize = 8 * canvas.scale;
    
    ctx.beginPath();
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - arrowSize * Math.cos(angle - Math.PI / 6),
      endY - arrowSize * Math.sin(angle - Math.PI / 6)
    );
    ctx.lineTo(
      endX - arrowSize * Math.cos(angle + Math.PI / 6),
      endY - arrowSize * Math.sin(angle + Math.PI / 6)
    );
    ctx.closePath();
    ctx.fillStyle = connection.color || connectionType.color;
    ctx.fill();
  };
  
  // ---------------------------------------------------------------------------
  // Render Fonksiyonları
  // ---------------------------------------------------------------------------
  
  // Düğüm HTML Render
  const renderNode = (node) => {
    const nodeType = ENDLESS_NOTES.NODE_TYPES[node.type] || ENDLESS_NOTES.NODE_TYPES.thought;
    const isSelected = selectedNodeIds.includes(node.id);
    const isHovered = hoveredNodeId === node.id;
    
    const nodeStyle = {
      width: nodeType.width,
      height: nodeType.height,
      backgroundColor: node.color || nodeType.color,
      transform: `translate(
        ${(node.position.offsetX + canvas.offsetX) * canvas.scale - (nodeType.width * canvas.scale / 2)}px, 
        ${(node.position.offsetY + canvas.offsetY) * canvas.scale - (nodeType.height * canvas.scale / 2)}px
      ) scale(${canvas.scale})`,
      zIndex: isSelected || isHovered ? 10 : 1,
      boxShadow: isSelected 
        ? '0 0 0 2px white, 0 0 0 4px #4f46e5' 
        : isHovered 
          ? '0 0 0 2px white, 0 0 0 4px #a5b4fc' 
          : 'none'
    };
    
    return (
      <div
        key={node.id}
        className="absolute rounded-lg p-3 cursor-pointer transition-shadow select-none"
        style={nodeStyle}
        onClick={(e) => handleNodeClick(e, node)}
        onMouseDown={(e) => handleNodeDragStart(e, node)}
        onMouseMove={(e) => handleNodeDrag(e, node)}
        onMouseUp={handleNodeDragEnd}
        onDoubleClick={(e) => {
          e.stopPropagation();
          handleNodeEdit(node);
        }}
        onMouseOver={() => setHoveredNodeId(node.id)}
        onMouseOut={() => setHoveredNodeId(null)}
      >
        <div className="w-full h-full flex flex-col overflow-hidden text-white">
          {/* Düğüm Başlığı */}
          <div className="flex justify-between items-center text-xs mb-1">
            <span className="font-semibold">
              {node.type.charAt(0).toUpperCase() + node.type.slice(1)}
            </span>
            {node.isStarred && <Star size={12} className="text-yellow-300" />}
          </div>
          
          {/* Düğüm İçeriği */}
          <div className="flex-1 overflow-auto text-sm">
            {node.content}
          </div>
          
          {/* Düğüm Etiketleri */}
          {node.tags && node.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {node.tags.map(tag => (
                <span key={tag} className="text-xs px-1 bg-white/20 rounded">
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Context Menü Render
  const renderContextMenu = () => {
    if (!contextMenu.isVisible) return null;
    
    const menuStyle = {
      position: 'fixed',
      left: `${contextMenu.x}px`,
      top: `${contextMenu.y}px`,
      zIndex: 1000
    };
    
    return (
      <div 
        ref={contextMenuRef}
        className="bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[150px]" 
        style={menuStyle}
      >
        {contextMenu.targetType === 'node' && (
          <>
            <button 
              className="w-full text-left px-4 py-1.5 hover:bg-gray-100 flex items-center text-sm"
              onClick={() => {
                const node = nodes.find(n => n.id === contextMenu.targetId);
                if (node) handleNodeEdit(node);
                setContextMenu(prev => ({ ...prev, isVisible: false }));
              }}
            >
              <Edit size={16} className="mr-2 text-gray-500" />
              Düzenle
            </button>
            
            <button 
              className="w-full text-left px-4 py-1.5 hover:bg-gray-100 flex items-center text-sm"
              onClick={() => {
                // Bağlantı başlatma
                const node = nodes.find(n => n.id === contextMenu.targetId);
                if (node) setConnectingNode(node);
                setCurrentAction(ENDLESS_NOTES.ACTIONS.CONNECT);
                setContextMenu(prev => ({ ...prev, isVisible: false }));
              }}
            >
              <Link size={16} className="mr-2 text-gray-500" />
              Bağlantı Oluştur
            </button>
            
            <button 
              className="w-full text-left px-4 py-1.5 hover:bg-gray-100 flex items-center text-sm"
              onClick={() => {
                deleteNode(contextMenu.targetId);
                setContextMenu(prev => ({ ...prev, isVisible: false }));
              }}
            >
              <Trash size={16} className="mr-2 text-gray-500" />
              Sil
            </button>
          </>
        )}
        
        {contextMenu.targetType === 'connection' && (
          <>
            <button 
              className="w-full text-left px-4 py-1.5 hover:bg-gray-100 flex items-center text-sm"
              onClick={() => {
                deleteConnection(contextMenu.targetId);
                setContextMenu(prev => ({ ...prev, isVisible: false }));
              }}
            >
              <Unlink size={16} className="mr-2 text-gray-500" />
              Bağlantıyı Sil
            </button>
          </>
        )}
        
        {contextMenu.targetType === 'canvas' && (
          <>
            <button 
              className="w-full text-left px-4 py-1.5 hover:bg-gray-100 flex items-center text-sm"
              onClick={() => {
                addNode({
                  clientX: contextMenu.x,
                  clientY: contextMenu.y
                });
                setContextMenu(prev => ({ ...prev, isVisible: false }));
              }}
            >
              <Plus size={16} className="mr-2 text-gray-500" />
              Not Ekle
            </button>
            
            <button 
              className="w-full text-left px-4 py-1.5 hover:bg-gray-100 flex items-center text-sm"
              onClick={() => {
                resetView();
                setContextMenu(prev => ({ ...prev, isVisible: false }));
              }}
            >
              <Move size={16} className="mr-2 text-gray-500" />
              Görünümü Sıfırla
            </button>
          </>
        )}
      </div>
    );
  };
  
  // Düğüm Düzenleme Modalı
  const renderNodeEditModal = () => {
    if (!editingNode) return null;
    
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
        <div 
          ref={nodeEditModalRef}
          className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] flex flex-col"
        >
          <div className="flex justify-between items-center border-b p-4">
            <h3 className="font-bold text-lg">
              {editingNode.content ? 'Notu Düzenle' : 'Yeni Not'}
            </h3>
            <button 
              className="p-1 rounded-full hover:bg-gray-100"
              onClick={handleNodeEditCancel}
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="p-4 flex-1 overflow-auto">
            {/* Not Türü Seçimi */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Not Türü
              </label>
              <div className="flex flex-wrap gap-2">
                {Object.keys(ENDLESS_NOTES.NODE_TYPES).map(type => (
                  <button
                    key={type}
                    className={`px-3 py-1.5 rounded-full text-sm ${
                      nodeEditForm.type === type
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                    onClick={() => handleNodeTypeChange(type)}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Not İçeriği */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                İçerik
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[120px] resize-none"
                placeholder="Not içeriğini girin..."
                value={nodeEditForm.content}
                onChange={(e) => setNodeEditForm(prev => ({ ...prev, content: e.target.value }))}
              />
            </div>
            
            {/* Not Etiketleri */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Etiketler
              </label>
              <div className="flex mb-2">
                <input
                  type="text"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Etiket ekle..."
                  value={nodeEditForm.tagInput}
                  onChange={(e) => setNodeEditForm(prev => ({ ...prev, tagInput: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <button
                  className="px-3 py-2 bg-indigo-600 text-white rounded-r-lg"
                  onClick={handleAddTag}
                >
                  Ekle
                </button>
              </div>
              
              {/* Etiketler Listesi */}
              <div className="flex flex-wrap gap-2">
                {nodeEditForm.tags.map(tag => (
                  <div key={tag} className="flex items-center bg-gray-100 rounded-full px-3 py-1">
                    <span className="text-sm">#{tag}</span>
                    <button
                      className="ml-1 text-gray-500 hover:text-gray-700"
                      onClick={() => handleRemoveTag(tag)}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Yıldızlı Not */}
            <div className="mb-4 flex items-center">
              <input
                type="checkbox"
                id="starred-checkbox"
                className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                checked={nodeEditForm.isStarred}
                onChange={(e) => setNodeEditForm(prev => ({ ...prev, isStarred: e.target.checked }))}
              />
              <label htmlFor="starred-checkbox" className="ml-2 text-sm text-gray-700">
                Yıldızlı Not
              </label>
            </div>
          </div>
          
          <div className="border-t p-4 flex justify-end space-x-2">
            <button
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700"
              onClick={handleNodeEditCancel}
            >
              İptal
            </button>
            <button
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:bg-indigo-300"
              onClick={handleNodeEditSave}
              disabled={!nodeEditForm.content.trim()}
            >
              Kaydet
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Arama Menüsü Render
  const renderSearchMenu = () => {
    if (!showSearch) return null;
    
    return (
      <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg border border-gray-200 p-2 z-10">
        <div className="flex items-center mb-2">
          <input
            type="text"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Notlarda ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            className="ml-2 p-2 rounded-full hover:bg-gray-100"
            onClick={() => setShowSearch(false)}
          >
            <X size={18} />
          </button>
        </div>
        
        {searchTerm && (
          <div className="max-h-60 overflow-y-auto">
            {filteredNodes.length > 0 ? (
              <div className="text-sm">
                {filteredNodes.map(node => (
                  <button
                    key={node.id}
                    className="w-full text-left p-2 hover:bg-gray-100 rounded"
                    onClick={() => {
                      setCanvas(prev => ({
                        ...prev,
                        offsetX: -node.position.offsetX,
                        offsetY: -node.position.offsetY
                      }));
                      setSelectedNodeIds([node.id]);
                      setShowSearch(false);
                      setSearchTerm('');
                    }}
                  >
                    <div className="font-medium">{node.content.substring(0, 50)}{node.content.length > 50 ? '...' : ''}</div>
                    <div className="text-xs text-gray-500 flex items-center">
                      <span className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: node.color }}></span>
                      {node.type.charAt(0).toUpperCase() + node.type.slice(1)}
                      {node.tags && node.tags.length > 0 && (
                        <span className="ml-2">
                          {node.tags.map(tag => `#${tag}`).join(' ')}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 p-2">
                Sonuç bulunamadı
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  
  // Araç Çubuğu Render
  const renderToolbar = () => {
    return (
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white rounded-full shadow-lg p-1 z-10 flex">
        <button
          className={`p-2 rounded-full ${currentAction === ENDLESS_NOTES.ACTIONS.MOVE ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-gray-100'}`}
          onClick={() => setCurrentAction(ENDLESS_NOTES.ACTIONS.MOVE)}
          title="Taşı"
        >
          <Move size={18} />
        </button>
        
        <button
          className={`p-2 rounded-full ${currentAction === ENDLESS_NOTES.ACTIONS.CONNECT ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-gray-100'}`}
          onClick={() => setCurrentAction(ENDLESS_NOTES.ACTIONS.CONNECT)}
          title="Bağlantı Oluştur"
        >
          <Link size={18} />
        </button>
        
        <button
          className="p-2 rounded-full text-gray-700 hover:bg-gray-100"
          onClick={() => addNode({
            clientX: containerRef.current.clientWidth / 2,
            clientY: containerRef.current.clientHeight / 2
          })}
          title="Not Ekle"
        >
          <Plus size={18} />
        </button>
        
        <button
          className="p-2 rounded-full text-gray-700 hover:bg-gray-100"
          onClick={() => setShowSearch(prev => !prev)}
          title="Ara"
        >
          <Search size={18} />
        </button>
        
        <span className="w-px h-6 bg-gray-200 mx-1"></span>
        
        <button
          className="p-2 rounded-full text-gray-700 hover:bg-gray-100"
          onClick={() => setIsGridVisible(prev => !prev)}
          title={isGridVisible ? "Izgarayı Gizle" : "Izgarayı Göster"}
        >
          <ChevronRight size={18} className={`transform ${isGridVisible ? 'rotate-90' : 'rotate-0'}`} />
        </button>
        
        <button
          className="p-2 rounded-full text-gray-700 hover:bg-gray-100"
          onClick={resetView}
          title="Görünümü Sıfırla"
        >
          <Move size={18} />
        </button>
      </div>
    );
  };
  
  // ---------------------------------------------------------------------------
  // Ana Render
  // ---------------------------------------------------------------------------
  
  useEffect(() => {
    if (canvasRef.current) {
      // Canvas boyutunu ayarla
      canvasRef.current.width = containerRef.current.clientWidth;
      canvasRef.current.height = containerRef.current.clientHeight;
      
      // İlk çizim
      drawCanvas();
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [drawCanvas]);
  
  return (
    <div 
      ref={containerRef}
      className={`relative w-full h-full overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}
    >
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 w-full h-full canvas-container"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onContextMenu={handleContextMenu}
        onDoubleClick={handleDoubleClick}
        onWheel={handleWheel}
      />
      
      {/* Düğümler */}
      {nodes.map(node => renderNode(node))}
      
      {/* Araç Çubuğu */}
      {renderToolbar()}
      
      {/* Arama Menüsü */}
      {renderSearchMenu()}
      
      {/* Context Menü */}
      {renderContextMenu()}
      
      {/* Düğüm Düzenleme Modalı */}
      {renderNodeEditModal()}
      
      {/* Yükleniyor Göstergesi */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-50">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
            <div className="mt-4 text-lg font-medium text-gray-700">Yükleniyor...</div>
          </div>
        </div>
      )}
      
      {/* Aktif Durum Göstergesi */}
      {currentAction !== ENDLESS_NOTES.ACTIONS.MOVE && (
        <div className="absolute top-4 right-4 bg-white/90 rounded-lg shadow px-3 py-2 text-sm font-medium z-10">
          {currentAction === ENDLESS_NOTES.ACTIONS.CONNECT && (
            <div className="flex items-center text-indigo-600">
              <Link size={16} className="mr-2" />
              Bağlantı Oluşturma Modu
              {connectingNode && (
                <span className="ml-1">- İkinci düğümü seçin</span>
              )}
            </div>
          )}
          <button
            className="mt-1 px-2 py-0.5 bg-gray-100 rounded text-xs"
            onClick={() => {
              setCurrentAction(ENDLESS_NOTES.ACTIONS.MOVE);
              setConnectingNode(null);
            }}
          >
            İptal (ESC)
          </button>
        </div>
      )}
    </div>
  );
});

export default EndlessNotesCanvas;